import { defineStore } from 'pinia';
import migrationApi from '@api/migration';

export const useMigrationStore = defineStore('migration', () => {
    const loading = ref(false);
    const tenantId = ref(null);
    const workbooks = ref([]);
    const currentWorkbook = ref(null);
    const previewData = ref([]);
    const sources = ref([]);
    const dispatching = ref(false);
    const allWorkbooks = ref([]);
    const workbooksPagination = ref(null);
    // Last query (filters + page) used for the cross-tenant list, so internal
    // refetches after a mutation preserve the operator's current view.
    const workbookQuery = ref({});
    // Company-grouped index: one entry per organisation with its active
    // workbook + previous (declined) ones. Remembers its query the same way.
    const companies = ref([]);
    const companiesPagination = ref(null);
    const companyQuery = ref({});
    const tenants = ref([]);

    function setTenantId(id) {
        tenantId.value = id;
    }

    function requireTenantId() {
        if (!tenantId.value) {
            throw new Error('Migration store: tenantId not set. Call setTenantId() before invoking actions.');
        }
        return tenantId.value;
    }

    async function fetchWorkbooks() {
        loading.value = true;
        try {
            const response = await migrationApi.getWorkbooks(requireTenantId());
            workbooks.value = response.data ?? [];
        } finally {
            loading.value = false;
        }
    }

    async function fetchWorkbook(id) {
        loading.value = true;
        try {
            const response = await migrationApi.getWorkbook(requireTenantId(), id);
            currentWorkbook.value = response.workbook ?? response;
        } finally {
            loading.value = false;
        }
    }

    async function fetchBatchCustomers(batchId) {
        const response = await migrationApi.getBatchCustomers(requireTenantId(), batchId);
        return response.data ?? [];
    }

    async function fetchPreview(batchId, count = 3, filter = null, orderId = null) {
        loading.value = true;
        previewData.value = [];
        try {
            const response = await migrationApi.getPreview(requireTenantId(), batchId, count, filter, orderId);
            previewData.value = response.data ?? [];
        } finally {
            loading.value = false;
        }
    }

    async function fetchWorkbookPreview(workbookId, count = 3, filter = null) {
        loading.value = true;
        previewData.value = [];
        try {
            const response = await migrationApi.getWorkbookPreview(requireTenantId(), workbookId, count, filter);
            previewData.value = response.data ?? [];
        } finally {
            loading.value = false;
        }
    }

    async function fetchWorkbookCustomers(workbookId) {
        const response = await migrationApi.getWorkbookCustomers(requireTenantId(), workbookId);
        return response.data ?? [];
    }

    async function fetchWorkbookAffiliates(workbookId) {
        const response = await migrationApi.getWorkbookAffiliates(requireTenantId(), workbookId);
        return response.data ?? [];
    }

    async function fetchMorePreviews(batchId, count = 3, filter = null) {
        loading.value = true;
        try {
            const response = await migrationApi.getPreview(requireTenantId(), batchId, count, filter);
            const newRecords = response.data ?? [];
            previewData.value = [...previewData.value, ...newRecords];
        } finally {
            loading.value = false;
        }
    }

    async function fetchMoreWorkbookPreviews(workbookId, count = 3, filter = null) {
        loading.value = true;
        try {
            const response = await migrationApi.getWorkbookPreview(requireTenantId(), workbookId, count, filter);
            const newRecords = response.data ?? [];
            previewData.value = [...previewData.value, ...newRecords];
        } finally {
            loading.value = false;
        }
    }

    async function resetTransformation(batchId) {
        loading.value = true;
        try {
            await migrationApi.resetTransformation(requireTenantId(), batchId);
            await fetchPreview(batchId);
        } finally {
            loading.value = false;
        }
    }

    async function fetchSources() {
        const response = await migrationApi.getSources();
        sources.value = response.sources ?? [];
        return sources.value;
    }

    async function fetchTenants() {
        const response = await migrationApi.getTenants();
        tenants.value = response.data ?? [];
        return tenants.value;
    }

    async function fetchAllWorkbooks(params) {
        // Passing params updates the remembered query; calling with none (e.g. an
        // internal refetch after a mutation) reuses the last filters + page.
        if (params !== undefined) {
            workbookQuery.value = params;
        }
        loading.value = true;
        try {
            const response = await migrationApi.getAllWorkbooks(workbookQuery.value);
            allWorkbooks.value = response.data ?? [];
            workbooksPagination.value = response.meta ?? null;
        } finally {
            loading.value = false;
        }
    }

    async function fetchCompanies(params) {
        // Mirrors fetchAllWorkbooks: passing params updates the remembered
        // query; an internal refetch (after create/delete) reuses it.
        if (params !== undefined) {
            companyQuery.value = params;
        }
        loading.value = true;
        try {
            const response = await migrationApi.getCompanies(companyQuery.value);
            companies.value = response.data ?? [];
            companiesPagination.value = response.meta ?? null;
        } finally {
            loading.value = false;
        }
    }

    async function fetchWorkbookFiles(workbookId) {
        const response = await migrationApi.getWorkbookFiles(workbookId);
        return response.data ?? { source: [], generated: [], final: [] };
    }

    async function searchWorkbook(workbookId, q) {
        const response = await migrationApi.searchWorkbook(workbookId, q);
        return { data: response.data ?? [], capped: response.capped ?? false };
    }

    // `silent` skips the global loading flag — used by the in-flight poll so a
    // background refresh every few seconds doesn't flicker every loading-gated
    // button (e.g. "Cancel import") disabled/enabled. User-triggered fetches
    // leave it false so their buttons still show progress.
    async function fetchMigration(workbookId, { silent = false } = {}) {
        // Switching to a DIFFERENT workbook: drop the previous one immediately so
        // the detail view shows its loading state rather than briefly flashing the
        // old workbook's data while the new fetch is in flight. Same-workbook
        // refetches (the in-flight poll, post-action refresh) keep the data put.
        if (currentWorkbook.value && String(currentWorkbook.value.id) !== String(workbookId)) {
            currentWorkbook.value = null;
        }
        if (!silent) {
            loading.value = true;
        }
        try {
            const response = await migrationApi.getMigration(workbookId);
            const workbook = response.workbook ?? response;
            currentWorkbook.value = workbook;
            // Set tenant context so the tenant-scoped actions/entries work on the detail page.
            if (workbook?.tenant_id) {
                setTenantId(workbook.tenant_id);
            }
            return workbook;
        } finally {
            if (!silent) {
                loading.value = false;
            }
        }
    }

    async function createMigration(targetTenantId, source) {
        dispatching.value = true;
        try {
            const response = await migrationApi.createMigration({
                target_tenant_id: targetTenantId,
                source,
            });
            await fetchCompanies();
            return response.workbook ?? response;
        } finally {
            dispatching.value = false;
        }
    }

    async function submitMigration(workbookId, files, uploadSeconds) {
        dispatching.value = true;
        try {
            const response = await migrationApi.submitMigration(workbookId, { files, upload_seconds: uploadSeconds });
            await fetchCompanies();
            return response.workbook ?? response;
        } finally {
            dispatching.value = false;
        }
    }

    async function retryMigration(workbookId) {
        dispatching.value = true;
        try {
            const response = await migrationApi.retryMigration(workbookId);
            await fetchCompanies();
            return response.workbook ?? response;
        } finally {
            dispatching.value = false;
        }
    }

    async function approve(tenantId, id) {
        const response = await migrationApi.approveWorkbook(tenantId, id);
        await fetchAllWorkbooks();
        return response;
    }

    async function rollback(tenantId, id) {
        const response = await migrationApi.rollbackWorkbook(tenantId, id);
        await fetchAllWorkbooks();
        return response;
    }

    async function cancelImport(tenantId, id) {
        const response = await migrationApi.cancelWorkbookImport(tenantId, id);
        await fetchAllWorkbooks();
        return response;
    }

    async function verifyRollback(tenantId, id) {
        return migrationApi.verifyWorkbookRollback(tenantId, id);
    }

    async function scanTenantOrphans(tenantId) {
        return migrationApi.scanTenantOrphans(tenantId);
    }

    async function decline(tenantId, id, reason) {
        const response = await migrationApi.declineWorkbook(tenantId, id, reason);
        await fetchAllWorkbooks();
        return response;
    }

    async function revive(tenantId, id) {
        const response = await migrationApi.reviveWorkbook(tenantId, id);
        await fetchAllWorkbooks();
        return response;
    }

    async function lockMigration(tenantId) {
        const response = await migrationApi.lockMigration(tenantId);
        if (currentWorkbook.value && String(currentWorkbook.value.tenant_id) === String(tenantId)) {
            currentWorkbook.value.migration_locked = true;
            currentWorkbook.value.migration_locked_at = response?.migration_locked_at ?? null;
        }
        await fetchAllWorkbooks();
        return response;
    }

    async function unlockMigration(tenantId) {
        const response = await migrationApi.unlockMigration(tenantId);
        if (currentWorkbook.value && String(currentWorkbook.value.tenant_id) === String(tenantId)) {
            currentWorkbook.value.migration_locked = false;
            currentWorkbook.value.migration_locked_at = null;
        }
        await fetchAllWorkbooks();
        return response;
    }

    async function consolidateWorkbook(tenantId, id) {
        loading.value = true;
        try {
            await migrationApi.consolidateWorkbook(tenantId, id);
            await fetchAllWorkbooks();
        } finally {
            loading.value = false;
        }
    }

    async function deleteWorkbook(tenantId, id) {
        await migrationApi.deleteWorkbook(tenantId, id);
        await fetchCompanies();
    }

    async function deleteAllWorkbooks() {
        await migrationApi.deleteAllWorkbooks(requireTenantId());
        await fetchWorkbooks();
    }

    // Per-workbook entry lists keyed by `${workbookId}:${status ?? 'all'}` so
    // the FAILED list and SKIPPED list for the same workbook can be open
    // simultaneously without trampling each other.
    const entriesByKey = ref({});
    const entriesLoading = ref({});

    function entryKey(workbookId, status) {
        return `${workbookId}:${status ?? 'all'}`;
    }

    async function fetchEntries(workbookId, { status = null, page = 1, perPage = 25 } = {}) {
        const key = entryKey(workbookId, status);
        entriesLoading.value[key] = true;
        try {
            const response = await migrationApi.getWorkbookEntries(requireTenantId(), workbookId, {
                status,
                page,
                perPage,
            });
            entriesByKey.value[key] = response;
            return response;
        } finally {
            entriesLoading.value[key] = false;
        }
    }

    function entriesFor(workbookId, status = null) {
        return entriesByKey.value[entryKey(workbookId, status)] ?? null;
    }

    function isEntriesLoading(workbookId, status = null) {
        return entriesLoading.value[entryKey(workbookId, status)] === true;
    }

    function replaceEntryInCache(updatedEntry) {
        // The updated entry's status may have flipped (e.g. FAILED → PENDING
        // on edit, PENDING → SKIPPED on skip). Drop it from every cached list
        // for its workbook so the next read fetches a fresh page that reflects
        // the new status filter.
        const workbookId = updatedEntry?.batch?.migration_workbook_id ?? null;
        Object.keys(entriesByKey.value).forEach((key) => {
            if (workbookId === null || key.startsWith(`${workbookId}:`)) {
                const cached = entriesByKey.value[key];
                if (cached?.data?.length) {
                    cached.data = cached.data.filter((e) => e.id !== updatedEntry.id);
                }
            }
        });
    }

    async function updateEntry(workbookId, entryId, entryData) {
        const response = await migrationApi.updateEntry(requireTenantId(), entryId, entryData);
        const updated = response.entry ?? response;
        replaceEntryInCache(updated);
        await fetchWorkbooks();
        return updated;
    }

    async function skipEntry(workbookId, entryId) {
        const response = await migrationApi.skipEntry(requireTenantId(), entryId);
        const updated = response.entry ?? response;
        replaceEntryInCache(updated);
        await fetchWorkbooks();
        return updated;
    }

    async function unskipEntry(workbookId, entryId) {
        const response = await migrationApi.unskipEntry(requireTenantId(), entryId);
        const updated = response.entry ?? response;
        replaceEntryInCache(updated);
        await fetchWorkbooks();
        return updated;
    }

    // Single-order trial import/rollback, driven from the order preview.
    async function importEntry(entryId) {
        const response = await migrationApi.importEntry(requireTenantId(), entryId);
        return response.entry ?? response;
    }

    async function rollbackEntry(entryId) {
        const response = await migrationApi.rollbackEntry(requireTenantId(), entryId);
        return response.entry ?? response;
    }

    function reset() {
        tenantId.value = null;
        workbooks.value = [];
        currentWorkbook.value = null;
        previewData.value = [];
        sources.value = [];
        dispatching.value = false;
        allWorkbooks.value = [];
        workbooksPagination.value = null;
        workbookQuery.value = {};
        companies.value = [];
        companiesPagination.value = null;
        companyQuery.value = {};
        tenants.value = [];
        entriesByKey.value = {};
        entriesLoading.value = {};
        loading.value = false;
    }

    return {
        loading,
        tenantId,
        workbooks,
        currentWorkbook,
        previewData,
        sources,
        dispatching,
        allWorkbooks,
        workbooksPagination,
        companies,
        companiesPagination,
        tenants,
        entriesByKey,
        setTenantId,
        fetchSources,
        fetchTenants,
        fetchAllWorkbooks,
        fetchCompanies,
        fetchWorkbookFiles,
        searchWorkbook,
        fetchMigration,
        createMigration,
        submitMigration,
        retryMigration,
        fetchWorkbooks,
        fetchWorkbook,
        fetchPreview,
        fetchBatchCustomers,
        fetchWorkbookCustomers,
        fetchWorkbookAffiliates,
        fetchWorkbookPreview,
        fetchMorePreviews,
        fetchMoreWorkbookPreviews,
        resetTransformation,
        approve,
        rollback,
        cancelImport,
        verifyRollback,
        scanTenantOrphans,
        decline,
        revive,
        lockMigration,
        unlockMigration,
        consolidateWorkbook,
        deleteWorkbook,
        deleteAllWorkbooks,
        fetchEntries,
        entriesFor,
        isEntriesLoading,
        updateEntry,
        skipEntry,
        unskipEntry,
        importEntry,
        rollbackEntry,
        reset,
    };
});
